import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Box, CircularProgress, Alert, Typography } from '@mui/material';
import axios from 'axios';
import { quizManifest } from '../data/quizManifest';

// Helper function to safely extract searchable text from various data formats
const getTextContent = (data) => {
    if (typeof data === 'string') {
        return data;
    }
    if (typeof data === 'object' && data !== null && data.text) {
        return data.text;
    }
    return ''; // Return empty string for invalid formats
};

const SmartSearch = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            setError('Please enter a search term.');
            return;
        }
        setLoading(true);
        setError('');

        try {
            const promises = quizManifest.map(file => axios.get(`/quizzes/${file}`));
            const responses = await Promise.all(promises);
            const allQuestions = responses.flatMap(response => response.data);

            const lowerCaseSearchTerm = searchTerm.toLowerCase();

            // --- UPGRADED SEARCH LOGIC ---
            const results = allQuestions.filter(q => {
                // 1. Check the question text
                const questionText = getTextContent(q.question).toLowerCase();
                if (questionText.includes(lowerCaseSearchTerm)) {
                    return true;
                }

                // 2. Check the explanation text
                const explanationText = getTextContent(q.explanation).toLowerCase();
                if (explanationText.includes(lowerCaseSearchTerm)) {
                    return true;
                }

                // 3. Check all of the options
                // .some() returns true if at least one option matches
                const hasMatchingOption = q.options.some(option =>
                    getTextContent(option).toLowerCase().includes(lowerCaseSearchTerm)
                );
                if (hasMatchingOption) {
                    return true;
                }

                // If no match is found in any field, filter it out
                return false;
            });

            if (results.length === 0) {
                setError('No questions found matching your search term.');
            } else {
                navigate('/search', { state: { results, searchTerm } });
            }
        } catch (err) {
            console.error("Smart Search failed to fetch quiz files:", err);
            setError('Failed to load quiz data for searching. Please check that all files in the manifest are correct.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box mt={4} p={3} border={1} borderColor="grey.400" borderRadius={2} sx={{ backgroundColor: 'background.paper' }}>
            <Typography variant="h6" gutterBottom>Smart Search</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Find questions across all topics containing a specific keyword.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                    fullWidth
                    label="Search for a keyword (e.g., 'elastase')"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button variant="contained" onClick={handleSearch} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Search'}
                </Button>
            </Box>
            {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
    );
};

export default SmartSearch;
